import React, { useState } from 'react';
import BottomSheet from './BottomSheet';

function fmtDate(s) {
  const str = String(s ?? '');
  if (str.length === 8) return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  return str;
}

function timeLabel(dateStr) {
  const str = String(dateStr ?? '');
  if (str.length !== 8) return { label: '과거', color: '#61758e' };
  const d = new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`);
  const diffDays = (Date.now() - d) / 86400000;
  if (diffDays < 60)  return { label: '최근', color: '#2458cf' };
  if (diffDays < 400) return { label: '1년 내', color: '#9a6507' };
  return { label: '과거', color: '#61758e' };
}

function resultColor(result) {
  if (!result) return null;
  if (result.includes('정상')) return '#0e9465';
  if (result.includes('의심') || result.includes('이상') || result.includes('암')) return '#cb3553';
  return null;
}

export default function NhisCancerScreening({ cancerScreenings }) {
  const [open, setOpen] = useState(false);

  if (!Array.isArray(cancerScreenings) || cancerScreenings.length === 0) return null;

  const latest = cancerScreenings[0];
  const preview = cancerScreenings.slice(0, 3);

  const uniqueTypes = [...new Set(cancerScreenings.map(s =>
    String(s.type ?? '').replace('암(', '').replace(')', '')
  ))].join('/') + ' 이력';

  const subHeader = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <span style={{
        fontSize: 11, fontWeight: 710, padding: '4px 8px', borderRadius: 999,
        background: '#e8f0ff', border: '1px solid #cfe0ff', color: '#2458cf',
      }}>
        총 {cancerScreenings.length}건
      </span>
      <span style={{
        fontSize: 11, fontWeight: 710, padding: '4px 8px', borderRadius: 999,
        background: '#f0eeff', border: '1px solid #ddd6ff', color: '#5b47e0',
      }}>
        {uniqueTypes}
      </span>
      {latest?.date && (
        <span style={{
          fontSize: 11, fontWeight: 710, padding: '4px 8px', borderRadius: 999,
          background: '#e8f0ff', border: '1px solid #cfe0ff', color: '#2458cf',
        }}>
          최신 {fmtDate(latest.date)}
        </span>
      )}
    </div>
  );

  return (
    <>
      <div style={{
        background: '#fff',
        border: '1px solid #dce7f2',
        borderRadius: 18,
        boxShadow: '0 10px 30px rgba(13,36,67,0.10)',
        padding: 14,
      }}>
        <div style={{ marginBottom: 9 }}>
          <span style={{ fontSize: 15, fontWeight: 760, letterSpacing: '-0.01em', color: '#102036' }}>
            암 검진 이력
          </span>
        </div>

        {/* Timeline */}
        <div style={{
          border: '1px solid #e7edf8',
          borderRadius: 12,
          background: 'linear-gradient(180deg, #fcfdff 0%, #f8fbff 100%)',
          padding: 10,
        }}>
          {preview.map((item, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '12px 1fr',
              gap: 8,
              margin: i === 0 ? '4px 0 8px' : '8px 0',
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: 'linear-gradient(135deg, #574eff, #1763ff)',
                boxShadow: '0 0 0 3px #ece8ff',
                marginTop: 4, flexShrink: 0, display: 'block',
              }} />
              <div>
                <b style={{ fontSize: 12, color: '#102036' }}>
                  {fmtDate(item.date)} · {item.type}
                </b>
                <span style={{ display: 'block', marginTop: 2, fontSize: 11, color: '#62758d' }}>
                  {item.facility}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Summary chips */}
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            fontSize: 11, fontWeight: 710, padding: '4px 8px', borderRadius: 999,
            background: '#edeaff', border: '1px solid #ddd6ff', color: '#4c46dd',
          }}>
            암 검진 이력 {cancerScreenings.length}건
          </span>
          {latest?.date && (
            <span style={{
              fontSize: 11, fontWeight: 710, padding: '4px 8px', borderRadius: 999,
              background: '#e8f0ff', border: '1px solid #cfe0ff', color: '#2458cf',
            }}>
              최근 {fmtDate(latest.date)}
            </span>
          )}
        </div>

        <button
          onClick={() => setOpen(true)}
          style={{
            marginTop: 12, width: '100%', height: 36, borderRadius: 8,
            border: '1px solid #1763ff', color: '#1763ff',
            background: 'transparent', fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          전체 보기
        </button>
      </div>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title="암 검진 이력 상세"
        subHeader={subHeader}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {cancerScreenings.map((item, i) => {
            const tl = timeLabel(item.date);
            const rc = resultColor(item.result);
            return (
              <div key={i} style={{
                padding: '12px 14px',
                background: '#f9fcff',
                border: '1px solid #e6edf7',
                borderRadius: 11,
              }}>
                {/* 상단: 타입+날짜·기관  |  시간 라벨 */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'flex-start' }}>
                  <div>
                    <b style={{ fontSize: 13, color: '#102036', display: 'block' }}>{item.type}</b>
                    <small style={{ fontSize: 11, color: '#62758d', marginTop: 2, display: 'block' }}>
                      {fmtDate(item.date)}{item.facility ? ` · ${item.facility}` : ''}
                    </small>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: tl.color,
                    background: `${tl.color}18`,
                    padding: '3px 8px', borderRadius: 999,
                    whiteSpace: 'nowrap', alignSelf: 'center',
                  }}>
                    {tl.label}
                  </span>
                </div>

                {/* 하단: kv pill — 판정 / 소견 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  <span style={{
                    fontSize: 10, padding: '4px 7px', borderRadius: 999,
                    border: '1px solid #dbe6f5', background: '#fff',
                    lineHeight: 1.3,
                    color: rc || '#4e6684',
                  }}>
                    <em style={{ fontStyle: 'normal', fontWeight: 730, color: '#2f4e72' }}>판정</em>
                    {' '}{item.result || '-'}
                  </span>
                  <span style={{
                    fontSize: 10, padding: '4px 7px', borderRadius: 999,
                    border: '1px solid #dbe6f5', background: '#fff', color: '#4e6684',
                    lineHeight: 1.3,
                  }}>
                    <em style={{ fontStyle: 'normal', fontWeight: 730, color: '#2f4e72' }}>소견</em>
                    {' '}{item.note || '-'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <p style={{ margin: '12px 2px 0', fontSize: 11, color: '#6f8299', lineHeight: 1.45 }}>
          암 검진 정보는 건강보험공단에서 수집된 데이터이며, 판정 결과가 없는 경우 원본에 미기재된 항목입니다.
        </p>
      </BottomSheet>
    </>
  );
}
